import axios from 'axios';
import { getAccountAccessError } from '../utils/accountAccess';
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://preview.caddiepay.app';
const NORMALIZED_BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, '');
export const BASE_WITHOUT_API = NORMALIZED_BACKEND_URL.replace(/\/api$/, '');

const buildClient = (baseURL) => axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const api = buildClient(`${BASE_WITHOUT_API}/api`);
const rootApi = buildClient(BASE_WITHOUT_API);
const pcmApi = buildClient(`${BASE_WITHOUT_API}/api/v1/pcm`);

const requestWithFallbacks = async (...requestFns) => {
  let lastError;

  for (let i = 0; i < requestFns.length; i += 1) {
    try {
      return await requestFns[i]();
    } catch (error) {
      lastError = error;
      const isLastAttempt = i === requestFns.length - 1;
      if (error?.response?.status !== 404 || isLastAttempt) {
        throw error;
      }
    }
  }

  throw lastError;
};

const attachAuthHeader = (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const handleAuthErrors = (error) => {
  const accountError = getAccountAccessError(error);
  if (error.response?.status === 401 || accountError) {
    const url = error.config?.url || '';
    const isAuthRoute = (
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/verify-otp') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password')
    );
    if (accountError) {
      // Keep existing login/registration toast consumers' string-detail contract.
      error.response.data = { ...error.response.data, code: accountError.code, detail: accountError.message };
    }
    if (!isAuthRoute || accountError) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    if (!isAuthRoute) {
      window.location.href = accountError ? `/login?reason=${accountError.code}` : '/login';
    }
  }
  return Promise.reject(error);
};

[api, rootApi, pcmApi].forEach((client) => {
  client.interceptors.request.use(attachAuthHeader);
  client.interceptors.response.use((response) => response, handleAuthErrors);
});

export const authAPI = {
  register: (data) => requestWithFallbacks(
    () => api.post('/auth/register', data),
    () => rootApi.post('/auth/register', data),
    () => pcmApi.post('/auth/register', data)
  ),
  login: (data) => requestWithFallbacks(
    () => api.post('/auth/login', data),
    () => rootApi.post('/auth/login', data),
    () => pcmApi.post('/auth/login', data)
  ),
  verifyOTP: (data) => requestWithFallbacks(
    () => api.post('/auth/verify-otp', data),
    () => rootApi.post('/auth/verify-otp', data),
    () => pcmApi.post('/auth/verify-otp', data)
  ),
  forgotPassword: (data) => requestWithFallbacks(
    () => api.post('/auth/forgot-password', data),
    () => rootApi.post('/auth/forgot-password', data),
    () => pcmApi.post('/auth/forgot-password', data)
  ),
  resetPassword: (data) => requestWithFallbacks(
    () => api.post('/auth/reset-password', data),
    () => rootApi.post('/auth/reset-password', data),
    () => pcmApi.post('/auth/reset-password', data)
  ),
  getMe: () => requestWithFallbacks(
    () => api.get('/auth/me'),
    () => rootApi.get('/auth/me'),
    () => pcmApi.get('/auth/me')
  ),
};

export const walletAPI = {
  getWallet: () => api.get('/wallet'),
  getPaymentSettings: () => api.get('/payment-settings'),
  sendMoney: (data) => api.post('/wallet/send', data),
  sendViaReference: (data) => api.post('/wallet/send-via-reference', data),
  resendCashPickupAtlasCode: (referenceId, idempotencyKey) => api.post(
    `/wallet/cash-pickups/${referenceId}/resend-atlas-code`,
    null,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  ),
  getTransactions: () => api.get('/transactions'),
  getMyCollectionReferences: () => api.get('/my-collection-references'),
  initiatePayfast: (data) => api.post('/payfast/initiate', data),
  checkPayfastStatus: (paymentId) => api.get(`/payfast/payment-status/${paymentId}`),
  verifyPayfastPayment: (paymentId) => api.post(`/payfast/verify-payment/${paymentId}`),
  initiateGatewayTopup: (provider, data) => api.post(`/topups/${provider}/initiate`, data),
  verifyGatewayTopup: (provider, paymentId) => api.post(`/topups/${provider}/verify-payment/${paymentId}`),
  getTopupProviders: () => api.get('/topups/providers'),
  getNotifications: () => api.get('/notifications'),
  markNotificationsRead: () => api.post('/notifications/mark-read'),
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribePush: (subscription) => api.post('/push/subscribe', { subscription }),
};

export const caddieAPI = {
  generateWithdrawalReference: (amount, idempotencyKey) => api.post(
    '/withdrawal/generate-reference',
    { amount },
    { headers: { 'Idempotency-Key': idempotencyKey } }
  ),
  getWithdrawalReferences: () => api.get('/withdrawal/references'),
  resendWithdrawalAtlasCode: (referenceId, idempotencyKey) => api.post(
    `/withdrawal/references/${referenceId}/resend-atlas-code`,
    null,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  ),
  searchCaddies: (query) => api.get(`/caddies/search?query=${query}`),
  registerCaddie: (data) => api.post('/registered-caddies', data),
  getRegisteredCaddies: () => api.get('/registered-caddies'),
  deleteRegisteredCaddie: (caddieId) => api.delete(`/registered-caddies/${caddieId}`),
  updateRegisteredCaddie: (caddieId, data) => api.put(`/registered-caddies/${caddieId}`, data),
  addExistingCaddie: (data) => api.post('/registered-caddies/add-existing', data),
};

export default api;
